import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import qs from "qs";

import { useSessionStore } from "@/entities/session/store";
import { SessionResponse } from "@/entities/session/api/api.dto";

type ExtraConfig = { _retry?: boolean; skipAuth?: boolean };
type Config = AxiosRequestConfig & ExtraConfig;

export class ClientApi {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing = false;
  private refreshQueue: ((token: string | null) => void)[] = [];
  private onUnauthorizedCallback?: () => void;

  constructor(baseURL: string, onUnauthorizedCallback?: () => void) {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    });
    this.onUnauthorizedCallback = onUnauthorizedCallback;
    this.setupInterceptors();
  }

  setOnUnauthorizedCallback(cb: () => void) {
    this.onUnauthorizedCallback = cb;
  }

  private getAccessToken() {
    return this.accessToken ?? useSessionStore.getState().accessToken;
  }

  private getRefreshToken() {
    return this.refreshToken ?? useSessionStore.getState().refreshToken;
  }

  private setupInterceptors() {
    this.client.interceptors.request.use((config) => {
      const accessToken = this.getAccessToken();

      if (accessToken && !(config as Config).skipAuth) {
        config.headers = config.headers ?? {};
        if (typeof config.headers.set === "function") {
          config.headers.set("Authorization", `Bearer ${accessToken}`);
        } else {
          (config.headers as Record<string, string>)["Authorization"] =
            `Bearer ${accessToken}`;
        }
      }
      return config;
    });

    this.client.interceptors.response.use(
      (res) => res,
      async (error: AxiosError) => {
        const config = (error.config as Config) ?? {};
        const isRefreshRequest = config.url?.includes("/authorization/refresh");

        if (error.response?.status === 401 && isRefreshRequest) {
          this.logout();
          useSessionStore.getState().logout();
          this.onUnauthorizedCallback?.();
          return Promise.reject(error);
        }

        if (
          error.response?.status === 401 &&
          !config?._retry &&
          this.getRefreshToken()
        ) {
          config._retry = true;
          try {
            const newToken = await this.refreshTokens();
            if (newToken) {
              return this.client(config);
            }
          } catch {
            this.logout();
            useSessionStore.getState().logout();
            this.onUnauthorizedCallback?.();
          }
        }
        return Promise.reject(error);
      },
    );
  }

  private async refreshTokens(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise((resolve) => this.refreshQueue.push(resolve));
    }

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return null;
    }

    this.isRefreshing = true;
    try {
      const response = await this.client.post<SessionResponse>(
        "/authorization/refresh",
        { refreshToken },
        { headers: {}, skipAuth: true } as Config,
      );
      const data = response.data;
      this.setTokens(data.accessToken, data.refreshToken);
      useSessionStore.getState().setAccessToken(data.accessToken);
      useSessionStore.getState().setRefreshToken(data.refreshToken);
      this.flushQueue(data.accessToken);
      return data.accessToken;
    } catch (e) {
      this.flushQueue(null);
      throw e;
    } finally {
      this.isRefreshing = false;
    }
  }

  private flushQueue(token: string | null) {
    this.refreshQueue.forEach((resolve) => resolve(token));
    this.refreshQueue = [];
  }

  logout() {
    this.accessToken = null;
    this.refreshToken = null;
    this.refreshQueue = [];
  }

  setTokens(access: string, refresh: string) {
    this.accessToken = access;
    this.refreshToken = refresh;
  }

  private withVersion(url: string, useV2?: boolean) {
    return useV2 ? `/v2${url}` : url;
  }

  get<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
    useV2?: boolean,
    config?: Config,
  ) {
    return this.client.get<T>(this.withVersion(url, useV2), {
      ...config,
      params,
    });
  }

  post<T = unknown, B = unknown>(
    url: string,
    body?: B,
    config?: Config,
    useV2?: boolean,
  ) {
    return this.client.post<T>(this.withVersion(url, useV2), body, config);
  }

  put<T = unknown, B = unknown>(url: string, body?: B, useV2?: boolean) {
    return this.client.put<T>(this.withVersion(url, useV2), body);
  }

  patch<T = unknown, B = unknown>(
    url: string,
    body?: B,
    config?: Config,
    useV2?: boolean,
  ) {
    return this.client.patch<T>(this.withVersion(url, useV2), body, config);
  }

  delete(url: string, useV2?: boolean) {
    return this.client.delete(this.withVersion(url, useV2));
  }

  head<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
    useV2?: boolean,
    config?: Config,
  ) {
    return this.client.head<T>(this.withVersion(url, useV2), {
      ...config,
      params,
    });
  }
}

export const apiInstance = new ClientApi(import.meta.env.VITE_API_HOST);
